import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useClickOutside } from "hooks/useClickOutside";
import { useTheme, Theme } from "hooks/useTheme";
import { IconCheck, IconChevronDown, IconDark, IconDesktop, IconLight, IconLightMode } from "icons";
import { AnalyticsEvent } from "utils";
import Portal from "components/Portal";

interface ThemeOption {
  id: Theme;
  icon: React.ReactNode;
  label: string;
}

interface Props {
  className?: string;
  collapsed?: boolean;
}

const ThemeToggle: React.FC<Props> = ({ className, collapsed }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });

  useClickOutside(menuRef, () => setIsOpen(false));

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    setMenuPos({
      bottom: window.innerHeight - rect.bottom,
      left: rect.right + 8,
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  const themeOptions: ThemeOption[] = [
    {
      id: "dark",
      icon: <IconDark />,
      label: t("common:theme_toggle.options.dark"),
    },
    {
      id: "light",
      icon: <IconLight />,
      label: t("common:theme_toggle.options.light"),
    },
    {
      id: "system",
      icon: <IconDesktop />,
      label: t("common:theme_toggle.options.system"),
    },
  ];

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const selectTheme = (themeId: Theme) => {
    AnalyticsEvent("Color_Theme", "", "click", { panel: themeId });
    setTheme(themeId);
    setIsOpen(false);
  };

  const selectedOption = themeOptions.find((option) => option.id === theme);
  const selectedLabel = selectedOption?.label || t("common:theme_toggle.options.system");

  return (
    <div className={clsx("relative", className)} ref={containerRef}>
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={clsx("flex items-center w-full gap-2 p-2 rounded text-trueBlack dark:text-grey hover:bg-black-200 transition-colors", collapsed && "justify-center")}
        aria-label={t("common:theme_toggle.menu_aria")}
        aria-expanded={isOpen}
        title={collapsed ? t("common:theme_toggle.label_template", { label: selectedLabel }) : undefined}
      >
        <IconLightMode className="w-[18px] h-[18px] flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="text-menu">{t("common:theme_toggle.label_template", { label: selectedLabel })}</span>
            <IconChevronDown className={clsx("w-[14px] h-[14px] ml-auto transition-transform", isOpen ? "rotate-90" : "-rotate-90")} />
          </>
        )}
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={menuRef}
            className="fixed flex flex-col gap-1 w-[198px] bg-trueWhite dark:bg-black border border-black-300 rounded-lg overflow-hidden p-1.5 z-[99999]"
            style={{ bottom: menuPos.bottom, left: menuPos.left }}
          >
            {themeOptions.map((option) => (
              <button
                key={option.id}
                className={clsx(
                  "flex items-center w-full px-2 py-2.5 hover:bg-black-200 text-left rounded transition-colors",
                  theme === option.id ? "text-trueBlack dark:text-blackGreen bg-[#E4E4E4] dark:bg-black-300" : "text-trueBlack dark:text-grey"
                )}
                onClick={() => selectTheme(option.id)}
                aria-label={t("common:theme_toggle.select_aria_template", { label: option.label })}
              >
                <span className="mr-4">{option.icon}</span>
                <span>{option.label}</span>
                {theme === option.id && <IconCheck className="ml-auto w-[18px] h-[18px]" />}
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default ThemeToggle;
